// Keyboard + pointer input. WASD/arrows to move, space to jump/dash, plus a
// few menu keys. Movement is reported as a normalized {x,z} on the ground plane.

// Standard Gamepad button indices (W3C mapping)
export const PAD = {
  SOUTH: 0, // A — confirm / jump
  EAST: 1, // B — back / slide
  WEST: 2,
  NORTH: 3,
  LB: 4,
  RB: 5,
  START: 9,
  DUP: 12,
  DDOWN: 13,
  DLEFT: 14,
  DRIGHT: 15,
} as const

export class Input {
  private keys = new Set<string>()
  private justPressed = new Set<string>()
  private justReleased = new Set<string>()
  pointerX = 0
  pointerY = 0
  pointerDown = false
  // FEAT-B: touch virtual-joystick vector (set by touchcontrols); overrides keyboard/pad when active
  touchMove = { x: 0, z: 0 }

  // ---- gamepad state ----
  gamepadActive = false // a pad is connected AND has been used at least once
  private padButtons: boolean[] = []
  private padJust = new Set<number>() // buttons that went down this frame
  private padAxes = [0, 0, 0, 0]
  private navLatch: '' | 'up' | 'down' | 'left' | 'right' = '' // stick nav debounce
  private navRepeatT = 0 // held-direction auto-repeat timer (menus)

  private onKeyDown = (e: KeyboardEvent) => {
    const k = e.code
    if (!this.keys.has(k)) this.justPressed.add(k)
    this.keys.add(k)
    // Stop the page from scrolling on arrows/space while playing.
    if (
      k === 'Space' ||
      k.startsWith('Arrow') ||
      k === 'Tab'
    ) {
      e.preventDefault()
    }
  }
  private onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code)
    this.justReleased.add(e.code)
  }
  private onPointerMove = (e: PointerEvent) => {
    this.pointerX = e.clientX
    this.pointerY = e.clientY
  }
  private onPointerDown = (e: PointerEvent) => {
    this.pointerDown = true
    this.pointerX = e.clientX
    this.pointerY = e.clientY
  }
  private onPointerUp = () => {
    this.pointerDown = false
  }
  private onBlur = () => {
    this.keys.clear()
  }

  attach(target: HTMLElement | Window = window): void {
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    window.addEventListener('pointermove', this.onPointerMove)
    window.addEventListener('pointerdown', this.onPointerDown)
    window.addEventListener('pointerup', this.onPointerUp)
    window.addEventListener('blur', this.onBlur)
  }

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerdown', this.onPointerDown)
    window.removeEventListener('pointerup', this.onPointerUp)
    window.removeEventListener('blur', this.onBlur)
  }

  down(code: string): boolean {
    return this.keys.has(code)
  }

  /** True only on the first frame a key went down since the last endFrame(). */
  pressed(code: string): boolean {
    return this.justPressed.has(code)
  }

  released(code: string): boolean {
    return this.justReleased.has(code)
  }

  /** Normalized movement vector from WASD/arrows, augmented by the left stick. */
  moveVector(): { x: number; z: number } {
    let x = 0
    let z = 0
    if (this.down('KeyW') || this.down('ArrowUp')) z -= 1
    if (this.down('KeyS') || this.down('ArrowDown')) z += 1
    if (this.down('KeyA') || this.down('ArrowLeft')) x -= 1
    if (this.down('KeyD') || this.down('ArrowRight')) x += 1
    const len = Math.hypot(x, z)
    if (len > 0) {
      x /= len
      z /= len
    }
    // FEAT-B: a held touch joystick wins over everything
    if (this.touchMove.x !== 0 || this.touchMove.z !== 0) return { x: this.touchMove.x, z: this.touchMove.z }
    // left stick overrides keyboard when pushed past the deadzone (analog magnitude kept)
    const pad = this.padMove()
    if (pad.x !== 0 || pad.z !== 0) return pad
    return { x, z }
  }

  // -------------------------------------------------------------------------
  // Gamepad
  // -------------------------------------------------------------------------

  /** Poll the first connected pad. Call once per frame BEFORE reading input. */
  pollGamepads(dt = 1 / 60): void {
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : []
    let pad: Gamepad | null = null
    for (const p of pads) {
      if (p && p.connected) {
        pad = p
        break
      }
    }
    this.padJust.clear()
    if (!pad) {
      this.padButtons = []
      this.padAxes = [0, 0, 0, 0]
      return
    }
    this.padAxes = [pad.axes[0] || 0, pad.axes[1] || 0, pad.axes[2] || 0, pad.axes[3] || 0]
    const prev = this.padButtons
    const cur = pad.buttons.map((b) => b.pressed || b.value > 0.5)
    for (let i = 0; i < cur.length; i++) {
      if (cur[i] && !prev[i]) this.padJust.add(i)
    }
    this.padButtons = cur
    // mark active once the player actually touches the pad (so we only show glyphs then)
    if (cur.some(Boolean) || Math.hypot(this.padAxes[0], this.padAxes[1]) > 0.3) this.gamepadActive = true
    // decay the menu auto-repeat timer
    if (this.navRepeatT > 0) this.navRepeatT = Math.max(0, this.navRepeatT - dt)
  }

  /** Left-stick movement with deadzone; magnitude preserved (clamped to 1) for analog speed. */
  padMove(): { x: number; z: number } {
    const dz = 0.25
    const x = this.padAxes[0]
    const z = this.padAxes[1]
    const m = Math.hypot(x, z)
    if (m < dz) return { x: 0, z: 0 }
    const clamp = Math.min(1, m) / m
    return { x: x * clamp, z: z * clamp }
  }

  /** True only on the frame a pad button went down. */
  padPressed(button: number): boolean {
    return this.padJust.has(button)
  }

  /** FEAT-C: right-stick horizontal axis (deadzoned) for free-camera yaw. */
  rightStickX(): number {
    const x = this.padAxes[2] || 0
    return Math.abs(x) < 0.2 ? 0 : x
  }

  /**
   * Menu navigation edge from the d-pad or left stick: returns a direction once
   * per press, with a hold-to-repeat after a short delay. Null when neutral.
   */
  padNav(): '' | 'up' | 'down' | 'left' | 'right' {
    // d-pad gives clean edges
    if (this.padPressed(PAD.DUP)) return 'up'
    if (this.padPressed(PAD.DDOWN)) return 'down'
    if (this.padPressed(PAD.DLEFT)) return 'left'
    if (this.padPressed(PAD.DRIGHT)) return 'right'
    // stick: latch a direction, auto-repeat while held
    const x = this.padAxes[0]
    const z = this.padAxes[1]
    let dir: typeof this.navLatch = ''
    if (Math.abs(x) > Math.abs(z)) {
      if (x > 0.55) dir = 'right'
      else if (x < -0.55) dir = 'left'
    } else {
      if (z > 0.55) dir = 'down'
      else if (z < -0.55) dir = 'up'
    }
    if (dir === '') {
      this.navLatch = ''
      return ''
    }
    if (dir !== this.navLatch) {
      this.navLatch = dir
      this.navRepeatT = 0.4 // initial delay before auto-repeat
      return dir
    }
    if (this.navRepeatT <= 0) {
      this.navRepeatT = 0.12 // repeat cadence
      return dir
    }
    return ''
  }

  /** Call once per frame AFTER reading input to clear edge-trigger sets. */
  endFrame(): void {
    this.justPressed.clear()
    this.justReleased.clear()
  }
}
